import { useState, useEffect } from "react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Calendar as CalendarIcon, Users } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PACKAGES } from "@/types/booking";
interface BookingFormProps {
  selectedPackageId?: string;
}
const BookingForm = ({
  selectedPackageId
}: BookingFormProps) => {
  const [packageId, setPackageId] = useState<string>(selectedPackageId || "");
  const [guests, setGuests] = useState<string>("");
  const [date, setDate] = useState<Date>();
  const [isProcessing, setIsProcessing] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  useEffect(() => {
    if (selectedPackageId) {
      setPackageId(selectedPackageId);
    }
  }, [selectedPackageId]);
  const selectedPackage = PACKAGES.find(pkg => pkg.id === packageId);
  const totalPrice = selectedPackage ? selectedPackage.price * parseInt(guests || "1") : 0;
  const handleBookNow = async () => {
    if (!packageId || !guests || !date) {
      toast({
        title: "Missing Information",
        description: "Please select package, number of guests, and travel date",
        variant: "destructive"
      });
      return;
    }
    setIsProcessing(true);

    // Fallback: if edge runtime can't reach Strabl (DNS), try from browser
    const createCartInBrowser = async (hosts: string[], paths: string[], payload: any) => {
      const defaultHosts = ["https://api.strabl.com", "https://sandbox.api.strabl.io", "https://sandbox.strabl.io", "https://checkout.strabl.io", "https://sandbox.checkout.strabl.io"];
      const defaultPaths = ["/v2/public/api/cart/", "/public/api/cart/", "/api/cart/", "/public/cart/"];
      const allHosts = Array.from(new Set([...(hosts || []), ...defaultHosts]));
      const allPaths = Array.from(new Set([...(paths || []), ...defaultPaths]));
      for (const base of allHosts) {
        for (const path of allPaths) {
          try {
            const url = `${base.replace(/\/+$/, "")}${path}`;
            const res = await fetch(url, {
              method: "POST",
              headers: {
                "Content-Type": "application/json"
              },
              body: JSON.stringify(payload)
            });
            if (res.ok) {
              const json = await res.json();
              const cartId = json?.data?.cartId || json?.cartId || json?.id;
              if (cartId) {
                const isSandbox = url.includes("sandbox");
                const checkoutBase = isSandbox ? "https://sandbox.checkout.strabl.io" : "https://checkout.strabl.io";
                window.location.href = `${checkoutBase}/?token=${cartId}`;
                return;
              }
            }
          } catch {
            // Network error - try next endpoint
          }
        }
      }
      toast({
        title: "Payment Error",
        description: "Unable to reach payment gateway. Please try again.",
        variant: "destructive"
      });
      setIsProcessing(false);
    };
    try {
      const {
        data,
        error
      } = await supabase.functions.invoke("create-strabl-payment", {
        body: {
          amount: totalPrice,
          packageId: packageId,
          packageName: selectedPackage?.name,
          guests: parseInt(guests),
          date: format(date, "yyyy-MM-dd")
        }
      });
      if (error) throw error;
      if (data?.checkoutUrl) {
        // Redirect to Strabl payment page
        window.location.href = data.checkoutUrl;
      } else if (data?.manualCheckout && data?.hosts && data?.paths && data?.payload) {
        await createCartInBrowser(data.hosts, data.paths, data.payload);
      } else if (data?.bookingId) {
        // Booking saved but payment not configured yet
        toast({
          title: "Booking Recorded",
          description: data.message || "Your booking has been saved successfully."
        });
        setIsProcessing(false);
      } else {
        throw new Error("No response received");
      }
    } catch {
      toast({
        title: "Payment Error",
        description: "Failed to process payment. Please try again.",
        variant: "destructive"
      });
      setIsProcessing(false);
    }
  };
  return <div className="bg-white rounded-lg shadow-2xl p-6 w-full max-w-md">
      <h2 className="text-xl font-bold mb-6 text-foreground">Secure Your Hot Air Balloon Ride </h2>

      <div className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="package" className="text-base font-medium">
            Select Package
          </Label>
          <Select value={packageId} onValueChange={setPackageId}>
            <SelectTrigger id="package" className="w-full h-12 bg-background">
              <SelectValue placeholder="Choose your package" />
            </SelectTrigger>
            <SelectContent className="bg-popover z-50">
              {PACKAGES.map(pkg => <SelectItem key={pkg.id} value={pkg.id}>
                  {pkg.name} - AED {pkg.price}
                </SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="guests" className="text-base font-medium">
            Guests
          </Label>
          <Select value={guests} onValueChange={setGuests}>
            <SelectTrigger id="guests" className="w-full h-12 bg-background">
              <SelectValue placeholder="No. of Guest" />
            </SelectTrigger>
            <SelectContent className="bg-popover z-50">
              {Array.from({
              length: 24
            }, (_, i) => i + 1).map(num => <SelectItem key={num} value={num.toString()}>
                  {num} {num === 1 ? "Guest" : "Guests"}
                </SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label className="text-base font-medium">Travel Date</Label>
          <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className={cn("w-full h-12 justify-start text-left font-normal bg-background", !date && "text-muted-foreground")}>
                <CalendarIcon className="mr-2 h-5 w-5" />
                {date ? format(date, "PPP") : "Pick a date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-popover z-50" align="start">
              <Calendar mode="single" selected={date} onSelect={selectedDate => {
              setDate(selectedDate);
              setDatePickerOpen(false);
            }} disabled={date => date < new Date()} initialFocus className="pointer-events-auto" />
            </PopoverContent>
          </Popover>
        </div>

        {selectedPackage && guests && <div className="bg-primary/5 rounded-lg p-4 space-y-2 border border-primary/10">
            <h3 className="font-semibold text-foreground mb-3">Booking Summary</h3>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Package</span>
              <span className="font-medium">{selectedPackage.name}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Price per person</span>
              <span className="font-medium">AED {selectedPackage.price}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Number of guests</span>
              <span className="font-medium">{guests}</span>
            </div>
            <div className="border-t border-primary/10 pt-2 mt-2">
              <div className="flex justify-between">
                <span className="font-semibold text-foreground">Total Price</span>
                <span className="font-bold text-primary text-lg">AED {totalPrice}</span>
              </div>
            </div>
          </div>}

        <Button onClick={handleBookNow} disabled={!packageId || !guests || !date || isProcessing} className="w-full h-12 text-base font-semibold mt-6 bg-accent hover:bg-accent/90">
          {isProcessing ? "Processing..." : "Book Now"}
        </Button>
      </div>
    </div>;
};
export default BookingForm;